/**
 * «Пригласить друга»: the client's own link, what it has brought so far, and
 * what each friend earns it (owner, 2026-10-29: five points of a friend's
 * profit, from the platform's fee, as fee credit).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Copy } from "lucide-react";
import { useState } from "react";

import { Button } from "@/design-system/Button";
import { Panel } from "@/design-system/Panel";
import { readReferral, requestReferralPayout, type ReferralResponse } from "@/shared/api";
import { atLeast, isZero, money, percent } from "@/shared/money/money";
import { plural } from "@/shared/text/plural";

/** Referral credit taken in USDT (owner, 2026-10-29): from the minimum on,
 *  the owner pays it by Bybit UID by hand. */
function Payout({ data, onAsked }: {
  readonly data: ReferralResponse; readonly onAsked: () => Promise<void>;
}): React.JSX.Element | null {
  const ask = useMutation({
    mutationFn: async () => {
      const { response } = await requestReferralPayout({});
      if (response?.status !== 204) throw new Error("refused");
    },
    onSuccess: onAsked,
  });
  const payable = data.payable ?? "0";
  const minimum = data.minimum_payout ?? "0";
  if (data.pending != null) {
    return <p data-testid="payout-pending">Заявка на выплату {money(data.pending)} USDT отправлена — платформа переведёт их на ваш Bybit UID.</p>;
  }
  const enough = atLeast(payable, minimum) && !isZero(minimum);
  return (
    <div className="flex flex-col gap-2 border-t border-border-subtle pt-3" data-testid="payout">
      <p>
        Можно получить в USDT: <b className="text-text-primary">{money(payable)} USDT</b>
        {!enough && ` · выплата от ${money(minimum)} USDT`}
        {data.paid_out != null && !isZero(data.paid_out) && ` · уже выплачено ${money(data.paid_out)} USDT`}
      </p>
      {enough && (
        <div>
          <Button type="button" variant="primary" disabled={ask.isPending} onClick={() => { ask.mutate(); }}>
            Запросить выплату {money(payable)} USDT
          </Button>
        </div>
      )}
      {ask.isError && <p role="alert" className="text-status-warning">Заявка не принята: проверьте, что Bybit подключён, и попробуйте позже.</p>}
    </div>
  );
}

export function InvitePanel(): React.JSX.Element {
  const [copied, setCopied] = useState(false);
  const query = useQuery({
    queryKey: ["referral"],
    retry: false,
    queryFn: async () => {
      const result = await readReferral({ cache: "no-store" });
      if (result.data === undefined) throw new Error("referral unavailable");
      return result.data;
    },
  });
  const data = query.data;
  const link = data === undefined ? "" : `${window.location.origin}/join/${data.code}`;
  return (
    <Panel title="Пригласить друга" testId="invite">
      <div className="flex flex-col gap-3 text-body-main text-text-secondary">
        {query.isPending ? <p role="status">Загружаю…</p> : query.isError || data === undefined ? (
          <p role="alert" className="text-status-warning">Ссылка сейчас недоступна. Обновите страницу.</p>
        ) : (
          <>
            <p>
              Платформа открыта только по приглашению. Отправьте другу ссылку: по ней он зарегистрируется и подключится. Когда он начнёт торговать со Skryptos, вы будете получать{" "}
              <b className="text-text-primary">{percent(data.share, 0)} от его прибыли</b> бонусом на оплату своей комиссии — пока он торгует.
            </p>
            <div className="flex flex-wrap items-center gap-2">
              <code className="min-w-0 break-all rounded-row bg-background-base px-2 py-1 font-mono text-data-mono text-text-primary" data-testid="invite-link">
                {link}
              </code>
              <Button
                type="button"
                size="sm"
                onClick={() => {
                  void navigator.clipboard.writeText(link).then(() => { setCopied(true); });
                }}
              >
                {copied ? <Check aria-hidden="true" className="size-4" /> : <Copy aria-hidden="true" className="size-4" />}
                {copied ? "Скопировано" : "Скопировать"}
              </Button>
            </div>
            <p data-testid="invite-result">
              {data.friends === 0
                ? "Вы ещё никого не пригласили."
                : `Приглашено: ${String(data.friends)} ${plural(data.friends, "друг", "друга", "друзей")} · начислено ${money(data.earned)} USDT бонуса.`}
            </p>
            <Payout data={data} onAsked={async () => { await query.refetch(); }} />
          </>
        )}
      </div>
    </Panel>
  );
}
